import SwiperLayout from '@/components/SwiperLayout';
import { Button, Flex, Image, message, Space, Tag } from 'antd';
import { useAtomValue } from 'jotai';
import { isMobileAtom } from '@/jotai/atoms/userAtom';
import { useEffect, useMemo, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import {
  approveArtistVerify,
  ArtistVerifyRejectReason,
  ArtistVerifyStatus,
  cancelArtistVerify,
  getArtistVerifyDetail,
  rejectArtistVerify,
  RejectArtistVerifyParams,
} from '@/api/artistVerify';
import { statusColorMap, statusTextMap } from './index';
import dayjs from 'dayjs';
import { FORMAT_TIME } from '@/types/common';
import RejectDialog from './rejectDialog';

const ActionPanelPage = () => {
  const navigator = useNavigate();
  const isMobile = useAtomValue(isMobileAtom);
  const [searchParams, setSearchParams] = useSearchParams();
  const [messageApi, contextHolder] = message.useMessage();

  const index = searchParams.get('index');
  const ids = useMemo(
    () => (searchParams.get('ids') ? searchParams.get('ids')?.split(',') : []),
    [searchParams]
  );

  const [currentIndex, setCurrentIndex] = useState<number>(
    index ? parseInt(index) : 0
  );
  const [currentId, setCurrentId] = useState<string>();
  const [rejectDialogOpen, setRejectDialogOpen] = useState(false);
  const [isCancelDialogOpen, setIsCancelDialogOpen] = useState(false);
  const [isLoading, setIsLoading] = useState(false);

  const labelStyle: React.CSSProperties = {
    fontWeight: 'bold',
    padding: isMobile ? 0 : '0 10px',
    flexShrink: 0,
  };

  useEffect(() => {
    if (ids && ids.length > 0) {
      setCurrentId(ids[currentIndex]);
    }
  }, [ids, currentIndex]);

  const handleChange = (index: number) => {
    const params = new URLSearchParams(searchParams);
    params.set('index', index.toString());
    setSearchParams(params);
    setCurrentIndex(index);
  };

  const { data: artistAbout, refetch: refetchArtistAbout } = useQuery({
    queryKey: ['artistAbout', currentId],
    queryFn: async () => {
      const resp = await getArtistVerifyDetail(currentId || '');
      if (resp.status !== 200) {
        return;
      }
      return resp.data;
    },
    enabled: !!currentId,
  });

  const renderStatus = (status?: ArtistVerifyStatus) => {
    if (typeof status === 'undefined') {
      return <Tag color="gray">暂无状态</Tag>;
    }
    return <Tag color={statusColorMap[status]}>{statusTextMap[status]}</Tag>;
  };

  const handleRejectDialogOk = async (
    reason: ArtistVerifyRejectReason,
    remark: string
  ) => {
    if (!currentId) {
      return;
    }
    setIsLoading(true);
    const params: RejectArtistVerifyParams = {
      certificationIds: [currentId],
      rejectReason: reason,
      rejectRemark: remark,
    };
    const resp = isCancelDialogOpen
      ? await cancelArtistVerify(params)
      : await rejectArtistVerify(params);

    setIsLoading(false);
    if (resp.status !== 200) {
      messageApi.error(resp.message);
      return;
    }
    messageApi.success(isCancelDialogOpen ? '取消认证成功' : '驳回认证成功');
    void refetchArtistAbout();
    setRejectDialogOpen(false);
  };

  const handleApprove = async () => {
    if (!currentId) {
      return;
    }
    setIsLoading(true);
    const resp = await approveArtistVerify([currentId]);
    setIsLoading(false);
    if (resp.status !== 200) {
      messageApi.error(resp.message);
      return;
    }
    messageApi.success('认证通过成功');
    void refetchArtistAbout();
  };

  const handleReject = () => {
    setIsCancelDialogOpen(false);
    setRejectDialogOpen(true);
  };

  const handleCancel = () => {
    setIsCancelDialogOpen(true);
    setRejectDialogOpen(true);
  };

  return (
    <div>
      {contextHolder}
      <SwiperLayout
        ids={ids || []}
        currentIndex={currentIndex}
        onChange={handleChange}
      >
        <Flex
          style={{
            marginBottom: 10,
            flexDirection: isMobile ? 'column' : 'row',
          }}
        >
          <div style={labelStyle}>认证 ID</div>
          <div>{currentId}</div>
        </Flex>
        <Flex
          style={{
            marginBottom: 10,
            flexDirection: isMobile ? 'column' : 'row',
          }}
        >
          <div style={labelStyle}>最近认证状态</div>
          <div>{renderStatus(artistAbout?.status)}</div>
        </Flex>
        <Flex
          style={{
            marginBottom: 10,
            flexDirection: isMobile ? 'column' : 'row',
          }}
        >
          <div style={labelStyle}>最近认证时间</div>
          <div>
            {artistAbout?.processTime
              ? dayjs(artistAbout.processTime).format(FORMAT_TIME)
              : ''}
          </div>
        </Flex>
        <Flex
          style={{
            marginBottom: 10,
            flexDirection: isMobile ? 'column' : 'row',
          }}
        >
          <div style={labelStyle}>用户真实姓名</div>
          <div>{artistAbout?.userName}</div>
        </Flex>
        <Flex
          style={{
            marginBottom: 10,
            flexDirection: isMobile ? 'column' : 'row',
          }}
        >
          <div style={labelStyle}>SUPP ID</div>
          <div>{artistAbout?.suppId}</div>
        </Flex>
        <Flex
          style={{
            marginBottom: 10,
            flexDirection: 'column',
          }}
        >
          <div style={labelStyle}>用户自我介绍</div>
          <div style={{ margin: '0 10px' }}>{artistAbout?.introduction}</div>
        </Flex>
        <div style={labelStyle}>证明文件（点击放大）</div>
        <Flex
          style={{
            margin: '10px',
            flexWrap: 'nowrap',
            overflowX: 'auto',
          }}
        >
          <Image.PreviewGroup>
            {artistAbout?.imageUrls.map((url, index) => (
              <Image src={url} width={150} key={index} />
            ))}
          </Image.PreviewGroup>
        </Flex>
      </SwiperLayout>
      <div style={{ textAlign: 'center', marginTop: 20 }}>
        <Space>
          {artistAbout?.status === ArtistVerifyStatus.PENDING && (
            <>
              <Button
                type="primary"
                onClick={handleApprove}
                disabled={isLoading}
              >
                认证通过
              </Button>
              <Button
                type="primary"
                danger
                onClick={handleReject}
                disabled={isLoading}
              >
                认证驳回
              </Button>
            </>
          )}
          {artistAbout?.status === ArtistVerifyStatus.APPROVED && (
            <Button
              type="primary"
              danger
              onClick={handleCancel}
              disabled={isLoading}
            >
              取消认证
            </Button>
          )}
          <Button onClick={() => navigator('/artist-verify')}>关闭页面</Button>
        </Space>
      </div>
      <RejectDialog
        dialogTitle={isCancelDialogOpen ? '取消认证原因' : '驳回认证原因'}
        isOpen={rejectDialogOpen}
        onClose={() => setRejectDialogOpen(false)}
        onConfirm={handleRejectDialogOk}
        isLoading={isLoading}
      />
    </div>
  );
};

export default ActionPanelPage;
